/**
 * User Onboarding API
 *
 * User-specific onboarding flow endpoints.
 * Shared endpoints (validate, start) are in shared-onboarding-api.ts
 */

import type { OnboardingFlowResponse } from '@/features/onboarding/types/onboarding.types';
import { unauthenticatedFetch } from './unauthenticated-fetch';

const API_BASE = '/user-portal-api/onboarding';

/**
 * Submit factor response
 */
export async function submitFactor(
  factorPayload: Record<string, unknown>
): Promise<OnboardingFlowResponse> {
  return unauthenticatedFetch<OnboardingFlowResponse>(`${API_BASE}/submit`, {
    method: 'POST',
    body: { factor_payload: factorPayload },
  });
}

/**
 * Resend OTP challenge
 */
export async function resendOtp(): Promise<{ success: boolean }> {
  return unauthenticatedFetch<{ success: boolean }>(`${API_BASE}/otp/resend`, {
    method: 'POST',
  });
}

/**
 * Get available alternative authenticators
 */
export async function getAuthenticatorAlternatives(): Promise<{
  alternatives: Array<{
    factorType: string;
    isEnrolled: boolean;
    displayName: string;
    description: string;
  }>;
}> {
  return unauthenticatedFetch(`${API_BASE}/authenticator-alternatives`, {
    method: 'GET',
  });
}

/**
 * Choose an authenticator from alternatives
 */
export async function chooseAuthenticator(
  factorType: string
): Promise<OnboardingFlowResponse> {
  return unauthenticatedFetch<OnboardingFlowResponse>(
    `${API_BASE}/choose-authenticator`,
    {
      method: 'POST',
      body: { authenticator: factorType },
    }
  );
}

/**
 * Complete onboarding flow
 */
export async function completeOnboarding(): Promise<OnboardingFlowResponse> {
  return unauthenticatedFetch<OnboardingFlowResponse>(`${API_BASE}/complete`, {
    method: 'POST',
  });
}

/**
 * Start device pairing during onboarding
 * Uses onboarding session cookie for authentication
 */
export async function startOnboardingDevicePairing(): Promise<{
  pairing_code: string;
  expires_at: string;
}> {
  return unauthenticatedFetch<{
    pairing_code: string;
    expires_at: string;
  }>(`${API_BASE}/device/start-pair`, {
    method: 'POST',
  });
}

/**
 * Get device pairing status during onboarding
 * Uses onboarding session cookie for authentication
 */
export async function getOnboardingDevicePairingStatus(
  pairingCode: string
): Promise<{
  status: string;
  expires_at: string;
}> {
  return unauthenticatedFetch<{
    status: string;
    expires_at: string;
  }>(`${API_BASE}/device/status/${pairingCode}`, {
    method: 'GET',
  });
}
